/* ==========================================================================
   TANGENT RECOMMENDATION CORE

   Shared runtime for quiz + gift recommendation pages.
   Page-specific behavior is selected via window.TangentRecoConfig (set in Liquid
   before this script loads).

   Config shape:
     mode: "quiz" | "gift"
     paths.recommendation: analytics / results page path
     selectors.heroContainer / heroContent
     fillerTreatImgUrl: treat filler background
     features.showCategoryLabel
     features.addButtonMobileClass
     features.productCardExtraClass
     features.fixedTabName  (e.g. "gift" — if set, tabNameForSection always returns it)

   Companion: sections/tangent-recommendation.liquid
              sections/tangent-gift-recommendation.liquid
   ========================================================================== */

; (function (window, document) {
  "use strict";

  const PAGE = Object.assign({
    mode: "quiz",
    paths: {
      recommendation: "/pages/routine-recommendation"
    },
    selectors: {
      heroContainer: ".haircare-hero",
      heroContent: ".haircare-hero__content"
    },
    fillerTreatImgUrl: "https://cdn.shopify.com/s/files/1/0434/1661/files/section_treat_filler_bg.png",
    features: {
      showCategoryLabel: true,
      addButtonMobileClass: true,
      productCardExtraClass: "",
      fixedTabName: null
    }
  }, window.TangentRecoConfig || {});

  const IS_GIFT = PAGE.mode === "gift";

  // --- Config: namespace ---
  // Interpolated into generated onclick attributes so markup and public API stay aligned.
  const NAMESPACE = "TangentReco";
  const EXPOSE_LEGACY_GLOBALS = true;

  // --- Config: text ---

  const TEXT = {
    ADD_TO_CART: "Add",
    ADD_ALL_TO_CART: "Add All to Cart",
    ADD_ALL_TO_BAG: "ADD ALL TO BAG",
    FLYOUT_ADD_TO_CART: "ADD TO BAG",
    BACK_TO_RESULTS: "BACK TO RESULTS",
    KEY_BENEFITS: "KEY BENEFITS",
    MORE_DETAILS: "More Details",
    SELECT_SIZE: "SELECT SIZE",
    FILLER_CARD_STARS: "★★★★★",
    NO_PRODUCTS_FOUND: "No products found. Please retry!",
    SOLD_OUT: "We're currently sold out of the selected product(s). Please check back later!",
    EMAIL_REQUIRED: "Please enter your email address.",
    EMAIL_INVALID: "Please enter a valid email address."
  };


  // --- Config: API & URLs ---
  const SHOP_ORIGIN = window.location.origin;

  const API = {
    CHECKOUT_PRODUCTS: "https://service.tangent.ai/shopify/get-checkout-products",
    QUIZ_RESPONSES: "https://service.tangent.ai/shopify/responses",
    ANALYTICS_TRACK: "https://s.tangent.ai/recommendation-track",
    SHOPIFY_CART_ADD: "/cart/add.js"
  };

  const PATHS = {
    CART_ADD: "/cart/add?",
    QUIZ: "/pages/routine-quiz",
    RECOMMENDATION: PAGE.paths.recommendation
  };

  // --- Config: selectors & element ids ---

  const ELEMENT_IDS = {
    DRAWER_OVERLAY: "drawerOverlay",
    SHOP_MARKET: "tangent-shop-market",
    CLIENT_CURRENCY: "tangent_client_currency",
    RECOMMENDATION_WRAPPER: "tangent_recommendation_parent_wrapper",
    SECTIONS_WRAPPER: "tangent_sections_wrapper",
    RESULTS_EMAIL_INPUT: "results-email",
    CHATBOT_WIDGET_ICON: "reamaze-widget-icon"
  };

  const SELECTORS = {
    // Page shell
    ESSENTIALS_TAB_PANEL: "#tab_essentials",
    FULLROUTINE_TAB_PANEL: "#tab_fullroutine",
    LOADER_CONTAINER: ".tangent-loader-container",
    HERO_CONTAINER: PAGE.selectors.heroContainer,
    HERO_CONTENT: PAGE.selectors.heroContent,
    TAB_CONTAINER: "#tab-wrapper",
    SECTIONS_WRAPPER: "#tangent_sections_wrapper",
    GENERAL_TEXT: "#tangent-general-text",
    TOAST_MESSAGE: "#message-text",
    LOAD_ON_DATA: ".load-on-data",
    USER_NAME: ".tangent-user-name",

    // Tabs
    TAB_BUTTON: ".tab-btn",
    ESSENTIAL_TAB_BUTTON: ".essential_btn",
    FULLROUTINE_TAB_BUTTON: ".fullroutine_btn",

    // Add all to bag
    ADD_ALL_TO_BAG_BLOCK: ".add-all-to-bag-block",
    ADD_ALL_TO_BAG_BTN: ".add-all-to-bag-btn",

    // Product card
    PRODUCT_CARD: ".tangent-product-card",
    PRODUCT_CONTENT: ".tangent-product-content",
    PRODUCT_IMAGE: ".tangent_product_image",
    PRODUCT_PRICE_TEXT: ".tangent_product_price_text",
    PRODUCT_TEXT_LINK: "a.tangent-text-link",
    MORE_DETAIL_BTN: ".more-detail-btn",
    ADD_TO_CART_BTN: ".tangent_add_to_cart_button",
    ADD_TO_CART_BTN_WRAPPER: ".tangent-product-add-btn-wrapper",

    // Variant / size selection
    PRICE_BTN_BLOCK: ".price-btn-block",
    SELECT_SIZE_BLOCK: ".select-size-block",
    SIZE_OPTIONS_WRAPPER: ".tangent-variant-wrapper",
    PRODUCT_SIZE_OPTION: ".product-size-option",
    SELECT_OPTION_BTN: ".select-option-btn",

    // Drawer / modal
    DRAWER_CONTENT: ".tangent-drawer-content",
    ACCORDION_HEADER: ".product-accordion-header",
    ACCORDION_ITEM: ".product-accordion-item",
    CLINICAL_RESULTS_WRAPPER: ".clinical-results-wrapper",
    CLINICAL_RESULTS_TRACK: ".clinical-results-track",
    CLINICAL_RESULTS_PREV: ".clinical-results-nav.prev",
    CLINICAL_RESULTS_NEXT: ".clinical-results-nav.next",

    // Email capture
    EMAIL_RESULTS_BLOCK: ".tangent-email-results-block",
    EMAIL_FIELD: ".tangent-email-field",
    EMAIL_ERROR: ".email-error",

    // Cross-sell / recently viewed
    PRODUCT_CAROUSEL: '[id*="product_carousel_with_image"]',
    RECENTLY_VIEWED_SECTION: '[id*="recently_viewed"]',
    RECENTLY_VIEWED_SLIDE: "swiper-slide",

    // Explore More Bundles carousel (gift)
    BUNDLE_CAROUSEL: "#product-carousel-with-image-explore-more-bundles, [id*='product_carousel_with_image']",
    BUNDLE_SLIDE: "swiper-slide",
    GTM_VIEW_ITEM: "[data-view-item-for-gtm-events]"
  };

  // Ids generated at runtime, kept together so markup and lookups can't drift
  const DOM_ID = {
    sectionContainer: (section) => `${section}_container`,
    sectionTextContainer: (section) => `${section}_text_container`,
    sectionProductGrid: (section) => `${section}_product_grid`,
    sectionAddAllToCart: (section) => `${section}_add_all_to_cart`,
    sizeOption: (tabName, variantId) => `${tabName}_${variantId}`
  };

  // Class suffixed with the active tab so bag totals only count visible cards
  const addToCartTabClass = (tabName) => `addToCart_${tabName}`;


  // --- Config: SVG ---

  const DOWN_CHEVRON = `<svg width="10" height="6" viewBox="0 0 10 6"><path d="M1 1L5 5L9 1" stroke="currentColor" fill="none" stroke-width="1.5"/></svg>`;
  const UP_CHEVRON = `<svg width="10" height="6" viewBox="0 0 10 6"><path d="M1 5L5 1L9 5" stroke="currentColor" fill="none" stroke-width="1.5"/></svg>`;

  // NOTE: the indentation baked into the two multi-line SVGs below is
  // deliberate. They are interpolated into already-indented templates, and
  // keeping it here leaves the generated markup byte-for-byte unchanged.
  const ACCORDION_CHEVRON_SVG = `<svg width="10" height="6" viewBox="0 0 10 6">
                          <path
                            d="M1 1L5 5L9 1"
                            stroke="currentColor"
                            fill="none"
                            stroke-width="1.5"
                          />
                        </svg>`;

  const BENEFIT_CHECK_SVG = `<svg width="16" height="14" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M1 6L5 10L13 2"
                        stroke="currentColor"
                        stroke-width="1"
                        stroke-linecap="butt"
                        stroke-linejoin="miter"
                      />
                    </svg>`;


  // --- Config: tabs & categories ---
  // Single bucket backing one global "Add All To Cart" permalink
  const GLOBAL_ADD_ALL_SECTION_KEY = "section_all_products";
  const GIFT_TAB_NAME = "gift";

  const TAB = {
    ESSENTIAL: "essential",
    FULLROUTINE: "fullroutine"
  };

  // A section belongs to a tab when its name contains this token
  const SECTION_TOKEN = {
    ESSENTIAL: "essential",
    FULLROUTINE: "fullroutine"
  };

  const CATEGORY_LABELS = {
    "Treatment": "Repair",
    "Shampoo": "Cleanse",
    "Dry Shampoo": "Refresh",
    "Conditioner": "Condition",
    "Leave-In Conditioner": "Style",
    "Styler": "Style",
    "Hair Mask": "Repair",
    "Oil": "Style"
  };

  // --- Config: filler cards ---
  const FILLER_CARDS = {
    treat: {
      quote: "My hair feels AMAZING! Not just weighed down conditioned, but a healthy, restored soft!",
      imgUrl: PAGE.fillerTreatImgUrl,
      name: "GlammaP"
    },
    cleanse: {
      quote: "Truly didn’t expect such a difference after the very first wash already. The shampoo is thick and lathers so well, while the conditioner feels super creamy and nourishing.",
      imgUrl: "https://cdn.shopify.com/s/files/1/0434/1661/files/section_wash_filler_bg.jpg",
      name: "RothN"
    },
    style: {
      quote: "My hair feels so much softer and it looks a lot shinier, and I saw a difference in reducing frizz and flyaways.",
      imgUrl: "https://cdn.shopify.com/s/files/1/0434/1661/files/section_style_filler_bg.jpg",
      name: "Stmar"
    }
  };

  //filler card style based on section type
  const FILLER_CARD_STYLES = {
    treat: {
      span1: { position: "right", color: "#fff", size: "28px" },
      span2: { position: "right", color: "#000", size: "32px" }
    },
    cleanse: {
      span1: { position: "right", color: "#fff", size: "28px" },
      span2: { position: "right", color: "#fff", size: "32px" }
    },
    style: {
      span1: { position: "-90px", color: "#000", size: "28px" },
      span2: { position: "center", color: "#000", size: "32px" }
    }
  };


  // Add button is full width when there is nothing to choose between
  const ADD_BUTTON_WIDTH = {
    SINGLE_VARIANT: "auto",
    MULTI_VARIANT: "130px"
  };

  // --- State ---

  const state = {
    // Product ID -> selected Variant ID
    selectedVariantByProduct: undefined,

    // Raw products straight off the API response
    recommendedProducts: undefined,

    // Product ID -> product with unavailable variants stripped out
    productsById: {},

    // Section key -> { count, cart_link, list } used to build cart permalinks
    addAllToCartBySection: {},

    shopMarket: undefined,
    quizResponseId: undefined,
    currencySymbol: undefined,
    activeTab: "",
    analyticsConsent: true
  };


  // --- DOM references ---

  const overlay = document.getElementById(ELEMENT_IDS.DRAWER_OVERLAY);
  const emailResultBlock = document.querySelector(SELECTORS.EMAIL_RESULTS_BLOCK);
  const essentialsPanel = document.querySelector(SELECTORS.ESSENTIALS_TAB_PANEL);
  const fullRoutinePanel = document.querySelector(SELECTORS.FULLROUTINE_TAB_PANEL);
  const loaderContainer = document.querySelector(SELECTORS.LOADER_CONTAINER);
  const heroContainer = document.querySelector(SELECTORS.HERO_CONTAINER);
  const tabContainer = document.querySelector(SELECTORS.TAB_CONTAINER);
  const addAllToBagBlock = document.querySelector(SELECTORS.ADD_ALL_TO_BAG_BLOCK);
  const sectionsWrapper = document.getElementById(ELEMENT_IDS.SECTIONS_WRAPPER)
    || document.querySelector(SELECTORS.SECTIONS_WRAPPER);


  // --- Initialization ---
  function init() {
    state.quizResponseId = new URL(window.location.href).searchParams.get("resp_id");
    fetchRecommendations(state.quizResponseId);
  }

  // --- API ---

  // Fetch the recommendation payload from Tangent for the given response id
  function fetchRecommendations(responseId) {
    if (responseId == null) return;

    state.shopMarket = document.getElementById(ELEMENT_IDS.SHOP_MARKET).value;

    fetch(buildCheckoutProductsUrl(responseId))
      .then(data => {
        return data.json();
      })
      .then(response => {
        renderRecommendations(response);
        return response;
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  }

  function buildCheckoutProductsUrl(responseId) {
    return `${API.CHECKOUT_PRODUCTS}?resp_id=${responseId}&self_serve=true&multiple_variants=true&market=${state.shopMarket}`;
  }


  // --- Page orchestration ---
  // Builds the whole results page from the API response, then wires up cart
  // totals and analytics consent.
  function renderRecommendations(response) {
    if (hasLegacyProductSections(response?.product_sections)) {
      showLegacyQuizNotice();
      return;
    }

    state.recommendedProducts = response.products;

    const currencyFromDom = document.getElementById(ELEMENT_IDS.CLIENT_CURRENCY).value;
    state.currencySymbol = currencyFromDom == "" ? "$" : currencyFromDom;

    const productSections = response.product_sections;
    const textSections = response.text_sections;

    if (IS_GIFT) {
      if (textSections != undefined) {
        appendTextOnlySectionsToHero(productSections, textSections);
      }

      renderGiftProductsOrEmptyState(response, productSections);
      revealResultsPage();
      removeDuplicateProductfromBundle(response.products);
      requestAnalyticsConsent();
      return;
    }

    if (productSections != undefined) {
      buildProductSectionContainers(productSections, textSections);
    }

    if (textSections != undefined) {
      appendTextOnlySectionsToHero(productSections, textSections);
    }

    renderProductsOrEmptyState(response, productSections);

    if (response.form?.first_name) {
      const userNameEl = document.querySelector(SELECTORS.USER_NAME);
      if (userNameEl) {
        userNameEl.innerHTML = `${response.form.first_name}'s`;
      }
    }

    revealResultsPage(response);
    state.activeTab = applyInitialTab(response);
    showAddAllToBagBlock();
    toggleRecentlyViewedCarousel();
    requestAnalyticsConsent();
  }

  function renderProductsOrEmptyState(response, productSections) {
    if (state.recommendedProducts.length === 0) {
      document.querySelector(SELECTORS.GENERAL_TEXT).innerHTML = TEXT.NO_PRODUCTS_FOUND;
      return;
    }

    renderProductSections(state.recommendedProducts, productSections, response.selected_variants);
    bindAddToCartButtons();
    revealDataDependentElements();
  }

  // Swap the loader out for the results, and hide the email capture block if
  // we already have the shopper's address.
  function revealResultsPage(response) {
    if (loaderContainer) {
      loaderContainer.style.display = "none";
    }

    if (heroContainer) {
      heroContainer.style.display = "flex";
    }

    if (tabContainer) {
      tabContainer.style.display = "block";
    }

    if (!IS_GIFT && emailResultBlock) {
      emailResultBlock.style.display = response?.email_available ? "none" : "block";
    }
  }

  function hasLegacyProductSections(productSections) {
    // Old quiz section keys — presence means this page cannot render the response
    const legacyProductSectionKeys = [
      "section_treat_repair",
      "section_cleanse_condition",
      "section_style_protect"
    ];

    return legacyProductSectionKeys.some((key) => key in productSections);
  }

  function showLegacyQuizNotice() {
    const wrapper = document.getElementById(ELEMENT_IDS.RECOMMENDATION_WRAPPER);

    wrapper.innerHTML = `
              <div class="tangent_alt-hero">
                <div class="tangent_alt-hero__overlay">
                  <div class="tangent_alt-hero__content">
                    <span class="tangent_alt-hero__eyebrow">NEW QUIZ</span>
          
                    <h1 class="tangent_alt-hero__title">
                      Even better<br>
                      hair days.
                    </h1>
          
                    <p class="tangent_alt-hero__description">
                      We've upgraded our Hair Quiz to give you our most accurate,
                      personalized routine yet.
                    </p>
          
                    <a href="${PATHS.QUIZ}" class="tangent_alt-hero__button">
                      FIND YOUR MATCH
                    </a>
                  </div>
                </div>
              </div>
            `;
  }

  // --- Product data ---

  // Drops unavailable variants and folds in grouped sibling products, so a card
  // only ever offers sizes a shopper can actually buy.
  function withAvailableVariantsOnly(product) {
    const seenIds = new Set();
    const availableEdges = [];

    const addUniqueAvailable = (edges = []) => {
      for (const edge of edges) {
        const id = edge?.node?.id;
        if (!edge?.node?.availableForSale || !id || seenIds.has(id)) continue;
        seenIds.add(id);
        availableEdges.push(edge);
      }
    };

    const groupNodes = product.productGroup?.reference?.products?.references?.nodes ?? [];
    const publishedProducts = [...groupNodes, product].filter((p) => p?.publishedInContext);

    for (const publishedProduct of publishedProducts) {
      addUniqueAvailable(publishedProduct.variants?.edges);
    }

    return {
      ...product,
      variants: {
        ...product.variants,
        edges: availableEdges
      }
    };
  }

  function isProductAvailable(product, variant) {
    if (!product || !variant) return false;

    const status = product.status?.toUpperCase();

    return (
      status === "ACTIVE" &&
      variant?.availableForSale === true
    );
  }

  // gid://shopify/ProductVariant/34689777696840 -> 34689777696840
  function extractVariantId(gid) {
    if (!gid) return null;
    return gid.split('/').pop();
  }

  function findProductById(productId) {
    return state.recommendedProducts.find((product) => product.id == productId);
  }

  // Normalises both Storefront GraphQL and flat REST variant shapes
  function listProductVariants(product) {
    if (!product?.variants) return [];

    // GraphQL Storefront shape: { edges: [{ node }] }
    if (Array.isArray(product.variants.edges)) {
      return product.variants.edges
        .map((edge) => edge?.node)
        .filter(Boolean);
    }

    // REST / flat array shape: [{ id, ... }]
    if (Array.isArray(product.variants)) {
      return product.variants;
    }

    return [];
  }

  function variantIdsMatch(leftId, rightId) {
    return String(extractVariantId(leftId)) === String(extractVariantId(rightId));
  }

  function findProductByVariantId(variantId) {
    if (!variantId || !Array.isArray(state.recommendedProducts)) return null;

    return state.recommendedProducts.find((product) => {
      return listProductVariants(product).some((variant) =>
        variantIdsMatch(variant.id, variantId)
      );
    }) || null;
  }

  function findVariant(product, variantId = null) {
    if (!product) return null;

    const variants = listProductVariants(product);
    if (variants.length === 0) return null;

    if (variantId !== null) {
      return variants.find((variant) => variantIdsMatch(variant.id, variantId)) || null;
    }

    return variants[0];
  }

  // Prefers the variant the quiz picked; falls back to first variant if OOS or missing
  function pickInitialVariant(product, quizVariantIds) {
    const productVariants = product?.variants?.edges || [];

    if (productVariants.length === 0) {
      return null;
    }

    const variants = productVariants.map(edge => edge.node);

    if (quizVariantIds && quizVariantIds.length > 0) {
      const selectedVariantId = String(quizVariantIds[0]);

      const selectedVariant = variants.find(variant =>
        extractVariantId(variant.id) === selectedVariantId
      );

      if (selectedVariant) {
        return selectedVariant;
      }
    }

    return variants[0];
  }

  function getVariantPrice(variant) {
    return variant?.contextualPricing?.price?.amount || variant?.price;
  }

  function toSizeOptions(variantsData) {
    return (variantsData?.edges || []).map(({ node }) => ({
      id: extractVariantId(node.id),
      title: node.title,
      price: getVariantPrice(node),
      available: node.availableForSale
    }));
  }

  function resolveVariantImage(variantImage, product) {
    const fallbackVariantImageUrl = "https://file.tangent.ai/quiz-content/merchants/nitesh-dev-store/image/5009378275701413-no-image-2048-5e88c1b20e087fb7bbe9a3771824e743c244f437e4f8ba93bbf7b11b53f7824c_720x.webp";

    // 1. Variant image
    if (variantImage && variantImage.url) {
      return variantImage.url;
    }

    // 2. Product main image
    if (product?.images?.nodes?.length > 0) {
      return product.images.nodes[0].url;
    }

    // 3. Default fallback image
    return fallbackVariantImageUrl;
  }

  // --- Section containers ---

  // Creates one container per product section and files it under the right tab
  function buildProductSectionContainers(productSections, textSections) {
    for (const sectionName in productSections) {
      // Holds the section's intro copy plus its product grid
      const sectionContainer = createSectionContainer(sectionName);

      if (textSections != undefined) {
        prependSectionText(textSections, sectionContainer, sectionName);
      }

      sectionContainer.appendChild(createProductGridContainer(sectionName));

      if (sectionName.includes(SECTION_TOKEN.ESSENTIAL)) {
        essentialsPanel?.appendChild(sectionContainer);
      } if (sectionName.includes(SECTION_TOKEN.FULLROUTINE)) {
        fullRoutinePanel?.appendChild(sectionContainer);
      }
    }
  }

  function createSectionContainer(sectionName) {
    const sectionContainer = document.createElement("div");
    sectionContainer.id = DOM_ID.sectionContainer(sectionName);
    sectionContainer.classList.add("tangent_section_container");
    return sectionContainer;
  }

  function createProductGridContainer(sectionName) {
    const grid = document.createElement("div");

    grid.id = DOM_ID.sectionProductGrid(sectionName);
    grid.classList.add(
      "section_product_grid",
      `${sectionName}_product_grid`
    );

    return grid;
  }

  function prependSectionText(textSections, sectionContainer, sectionName) {
    const content = textSections?.[sectionName];

    if (!content || !Array.isArray(content)) return;

    const sectionDiv = document.createElement("div");
    sectionDiv.id = DOM_ID.sectionTextContainer(sectionName);
    sectionDiv.classList.add(
      "section_text_container",
      `${sectionName}_text_container`
    );

    sectionDiv.innerHTML = content.join(" ");
    sectionContainer.appendChild(sectionDiv);
  }

  // Text sections with no matching product section become hero copy
  function appendTextOnlySectionsToHero(productSections, textSections) {
    const heroContentBlock = document.querySelector(SELECTORS.HERO_CONTENT);

    for (const [sectionName, content] of Object.entries(textSections || {})) {
      if (productSections?.[sectionName] !== undefined) continue;
      if (!Array.isArray(content)) continue;

      heroContentBlock.insertAdjacentHTML("beforeend", content.join(""));
    }
  }


  // --- Product rendering ---

  function renderProductSections(products, sections, selectedVariants) {
    state.selectedVariantByProduct = {};

    Object.keys(sections).forEach(section => {
      renderSectionGrid(section, sections[section], products, selectedVariants, sections);
    });
  }

  function renderSectionGrid(section, productIds, products, selectedVariants, sections) {
    const grid = document.getElementById(DOM_ID.sectionProductGrid(section));
    const sectionContainer = document.getElementById(DOM_ID.sectionContainer(section));
    const productGridColumns = 3;

    if (!grid) return;

    // Build off-document, then attach in one go
    const fragment = document.createDocumentFragment();
    let renderedCount = 0;

    for (const id of productIds || []) {
      for (let index = 0; index < products.length; index++) {
        if (String(products[index].id) !== id) continue;

        if (appendProductCard(fragment, products[index], index, selectedVariants, section, sections)) {
          renderedCount++;
        }
      }
    }

    // Hide treat/cleanse/style (etc.) when nothing actually rendered in this section
    if (renderedCount === 0) {
      if (sectionContainer) sectionContainer.style.display = "none";
      return;
    }

    if (sectionContainer) sectionContainer.style.display = "";

    // Pad the trailing row of the 3-up grid with a review card.
    // Uses rendered count (not productIds.length) so unavailable products don't skew padding.
    const remaining = renderedCount % productGridColumns;

    if (remaining !== 0) {
      fragment.appendChild(buildFillerCard(remaining, section));
    }

    grid.appendChild(fragment);
  }

  function appendProductCard(fragment, rawProduct, index, selectedVariants, section, sections) {
    const product = withAvailableVariantsOnly(rawProduct);
    state.productsById[product.id] = product;

    const variant = pickInitialVariant(product, selectedVariants[product.id]);

    if (!isProductAvailable(product, variant)) {
      console.log("product not available", product.title);
      return false;
    }

    state.selectedVariantByProduct[product.id] = extractVariantId(variant.id);

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = renderProductCard(product, index, variant, section, sections);

    while (tempDiv.firstChild) {
      fragment.appendChild(tempDiv.firstChild);
    }

    return true;
  }

  function renderProductCard(product, index, variant, section, sections) {
    const variantId = extractVariantId(variant.id);
    const variantImageUrl = resolveVariantImage(variant.image, product);
    const variantProductUrl = `/products/${product.handle}?variant=${variantId}&source=quiz`;
    const hasOnlyOneVariant = product.variants.edges.length == 1;

    // Hardcoded section to maintain one global "Add All To Cart"
    registerVariantForAddAll(
      GLOBAL_ADD_ALL_SECTION_KEY,
      state.selectedVariantByProduct[product.id]
    );

    return `
              <div class="tangent-product-card ${PAGE.features.productCardExtraClass || ""}">
          
                <div id="tangent-image-product-card">
                  <a
                    id="img_url_tangent_product_${index}"
                    class="tangent-text-link"
                    target="_blank"
                    href="${variantProductUrl}"
                  >
                    <img
                      id="image_tangent_product_${index}"
                      src="${variantImageUrl}"
                      class="tangent_product_image"
                      alt="${escapeHTML(product.title)}"
                    >
                  </a>
                </div>
                <div class="tangent-product-content">
                  <a
                    id="title_url_tangent_product_${index}"
                    class="tangent-text-link"
                    target="_blank"
                    href="${variantProductUrl}"
                  >
                    ${PAGE.features.showCategoryLabel ? renderCategoryLabel(product) : ""}
                    <p class="tangent-product-title tangent_product_title_text">
                      ${escapeHTML(product.title)}
                    </p>
                  </a>
                  ${renderShortDescription(product)}
                  ${renderStarRating(variant)}
                  <div class="price-btn-block">
                    <p class="tangent_product_price_text">${formatVariantPriceRange(product.variants)}</p>
                    <div class="select-size-block">
                      ${hasOnlyOneVariant ? '' : renderSizeOptions(product.variants, section)}
                      ${renderAddToCartButton(product, variant, variantId, section, hasOnlyOneVariant)}
                    </div>
                  </div>
                </div>
              </div>
            `;
  }

  function renderShortDescription(product) {
    if (!product?.shortDescription?.value) return "";

    return `<p class="product-short-description">${trimWords(product?.shortDescription?.value)}
                    <button class="more-detail-btn" data-productid="${product.id}" onclick="${NAMESPACE}.openProductDetail(this)">${TEXT.MORE_DETAILS}</button>
                  </p>`;
  }

  function renderAddToCartButton(product, variant, variantId, section, hasOnlyOneVariant) {
    const isAvailable = isProductAvailable(product, variant);
    const tabName = tabNameForSection(section);
    const width = hasOnlyOneVariant
      ? ADD_BUTTON_WIDTH.SINGLE_VARIANT
      : ADD_BUTTON_WIDTH.MULTI_VARIANT;

    return `<button
                        class="tangent-product-add-btn-wrapper tangent_add_to_cart_button${PAGE.features.addButtonMobileClass ? " show-on-mobile-only" : ""} ${addToCartTabClass(tabName)}"
                        id="${variantId}"
                        data-price="${getVariantPrice(variant)}"
                        type="button"
                        style="display: ${hasOnlyOneVariant ? 'block' : 'none'};width:${width};"
                        ${!isAvailable ? "disabled" : ""}>
                          ${TEXT.ADD_TO_CART}
                      </button>`;
  }

  function renderCategoryLabel(product) {
    const productCategory = safeJsonParse(product?.productCategory?.value, []);
    if (!Array.isArray(productCategory) || !productCategory.length) return "";

    const title = CATEGORY_LABELS[productCategory[0]] || "";
    return title ? `<p class="pruduct-category-title">${title}</p>` : "";
  }

  // Bazaarvoice rating widget, hydrated client side from the variant SKU
  function renderStarRating(variant) {
    return `<product-star-rating class='block'>
              <div
                class='px-lg lg:px-0 mb-3 lg:mb-4'
                data-bv-show='rating_summary'
                data-bv-product-id='${variant.sku}'
              ></div>
            </product-star-rating>`;
  }

  function formatVariantPriceRange(variantsData) {
    const variants = variantsData?.edges || [];

    if (!variants.length) return "";

    const prices = variants
      .map(edge => parseFloat(getVariantPrice(edge?.node)))
      .filter(price => !isNaN(price));

    if (!prices.length) return "";

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    return minPrice === maxPrice
      ? formatPrice(minPrice)
      : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
  }

  function formatPrice(price) {
    return `${state.currencySymbol}${Number.isInteger(price) ? price : price.toFixed(2)}`;
  }


  // --- Filler cards ---

  function removeDuplicateProductfromBundle(products) {
    if (!products?.length) return;

    const recommendedIds = new Set(
      products
        .map((product) => String(extractVariantId(product.id)))
        .filter(Boolean)
    );

    if (!recommendedIds.size) return;

    const carousels = document.querySelectorAll(SELECTORS.BUNDLE_CAROUSEL);

    carousels.forEach((carousel) => {
      const slides = carousel.querySelectorAll(SELECTORS.BUNDLE_SLIDE);

      slides.forEach((slide) => {
        const itemId = getSlideProductId(slide);

        if (itemId && recommendedIds.has(String(itemId))) {
          slide.remove();
        }
      });

      carousel.querySelector("swiper-container")?.swiper?.update();
    });
  }

  function getSlideProductId(slide) {
    const gtmHost = slide.querySelector(SELECTORS.GTM_VIEW_ITEM)
      || slide.shadowRoot?.querySelector(SELECTORS.GTM_VIEW_ITEM);

    if (!gtmHost) return null;

    try {
      return JSON.parse(gtmHost.getAttribute("data-view-item-for-gtm-events") || "{}").item_id;
    } catch (error) {
      return null;
    }
  }

  function renderGiftProductsOrEmptyState(response, productSections) {
    if (!sectionsWrapper) return;

    sectionsWrapper.innerHTML = "";

    if (!state.recommendedProducts?.length) {
      sectionsWrapper.innerHTML = `<p class="tangent-gift-empty">${TEXT.NO_PRODUCTS_FOUND}</p>`;
      return;
    }

    const primary = getPrimaryProductInfo(productSections);

    if (!primary) {
      sectionsWrapper.innerHTML = `<p class="tangent-gift-empty">${TEXT.NO_PRODUCTS_FOUND}</p>`;
      return;
    }

    state.selectedVariantByProduct = {};

    const grid = document.createElement("div");
    grid.className = "tangent-gift-result-grid";

    const productSlot = document.createElement("div");
    productSlot.className = "tangent-gift-product-slot";

    const fillerSlot = document.createElement("div");
    fillerSlot.className = "tangent-gift-filler-slot";

    const fragment = document.createDocumentFragment();
    let rendered = false;

    for (let index = 0; index < state.recommendedProducts.length; index++) {
      const product = state.recommendedProducts[index];

      if (String(product.id) !== String(primary.productId)) continue;

      rendered = appendProductCard(
        fragment,
        product,
        index,
        response.selected_variants || {},
        primary.sectionName,
        productSections
      );
      break;
    }

    if (!rendered) {
      sectionsWrapper.innerHTML = `<p class="tangent-gift-empty">${TEXT.NO_PRODUCTS_FOUND}</p>`;
      return;
    }

    productSlot.appendChild(fragment);
    fillerSlot.appendChild(buildGiftFillerCard());

    grid.appendChild(productSlot);
    grid.appendChild(fillerSlot);
    sectionsWrapper.appendChild(grid);

    bindAddToCartButtons();
  }

  function getPrimaryProductInfo(productSections) {
    if (!productSections) return null;

    const preferredSectionKeys = ["section_main_products", "section_gift_product"];

    for (const sectionName of preferredSectionKeys) {
      const productIds = productSections[sectionName];

      if (Array.isArray(productIds) && productIds.length > 0) {
        return { sectionName, productId: productIds[0] };
      }
    }

    for (const sectionName in productSections) {
      const productIds = productSections[sectionName];

      if (Array.isArray(productIds) && productIds.length > 0) {
        return { sectionName, productId: productIds[0] };
      }
    }

    return null;
  }

  function buildGiftFillerCard() {
    const content = FILLER_CARDS.treat;
    const { size } = FILLER_CARD_STYLES.treat.span1;

    const card = document.createElement("div");

    card.classList.add("filler-card", "filler_card_gift", "tangent-gift-filler-card");
    card.style.backgroundImage = `url('${content.imgUrl}')`;

    card.innerHTML = `
              <div class="filler-card__overlay">
                <div class="filler-card__stars filler-card__stars_gift">${TEXT.FILLER_CARD_STARS}</div>
                <div class="filler-card-quote-block">
                  <p class="filler-card__quote filler-card__quote_gift" style="font-size:${size};">"${content.quote}"</p>
                  <p class="quote-name quote-name_gift">~${content.name}</p>
                </div>
              </div>
          `;

    return card;
  }

  function buildFillerCard(remainingSlots, section) {
    const sectionType = sectionTypeOf(section);
    const content = FILLER_CARDS[sectionType] || null;
    const span = remainingSlots == 1 ? 2 : 1;
    const { color, size, position } = FILLER_CARD_STYLES[sectionType][`span${span}`];

    const card = document.createElement("div");

    card.classList.add("filler-card", `filler_card_${sectionType}`, `filler-card--span-${span}`);
    card.style.backgroundImage = `url('${content.imgUrl}')`;
    card.style.backgroundPosition = position;

    card.innerHTML = `
                <div class="filler-card__overlay">
                  <div class="filler-card__stars filler-card__stars_${sectionType}" style="color: ${color};">${TEXT.FILLER_CARD_STARS}</div>
                  <div class="filler-card-quote-block">
                    <p class="filler-card__quote filler-card__quote_${sectionType}" style="font-size:${size}; color: ${color};">"${content.quote}"</p>
                    <p class="quote-name quote-name_${sectionType}" style="color: ${color};">-${content.name}</p>
                  </div>
                </div>
            `;

    return card;
  }

  // Trailing token of a section name: cleanse, treat, style
  function sectionTypeOf(sectionName) {
    return sectionName.split("_").pop();
  }


  // --- Variant / size selection ---

  function renderSizeOptions(variants, section) {
    const sizeOptions = toSizeOptions(variants);
    const tabName = tabNameForSection(section);

    return `
              <div class="tangent-variant-wrapper">
                ${sizeOptions.map((option) => `
                  <button class="product-size-option" id="${DOM_ID.sizeOption(tabName, option.id)}" data-price="${option.price}" onclick="${NAMESPACE}.selectVariant(this)">
                    ${option.title}
                  </button>
                `).join("")}
              </div>
      
              <button
                class="select-option-btn"
                onclick="${NAMESPACE}.toggleSizeOptions(this)"
              >
                ${TEXT.SELECT_SIZE} ${DOWN_CHEVRON}
              </button>
          `;
  }

  // Show / hide the whole size-options panel above the select button
  function toggleSizeOptions(button) {
    const wrapper = button
      .closest(SELECTORS.SELECT_SIZE_BLOCK)
      ?.querySelector(SELECTORS.SIZE_OPTIONS_WRAPPER);

    if (!wrapper) return;

    const isOpen = wrapper.classList.toggle("is-open");
    button.innerHTML = `${TEXT.SELECT_SIZE} ${isOpen ? UP_CHEVRON : DOWN_CHEVRON}`;
  }

  function selectVariant(button) {
    const variantId = variantIdFromSizeOption(button);
    const variantPrice = button.dataset.price;

    updateCardLinks(button, variantId);
    updateCardImage(button, variantId);
    updateCardPrice(button, variantPrice);
    markSizeOptionSelected(button);
    promoteAddButtonForVariant(button, variantId, variantPrice);

    refreshAddAllToBagLabel();
  }

  function updateCardPrice(button, variantPrice) {
    const priceBlock = button.closest(SELECTORS.PRICE_BTN_BLOCK);
    const priceEl = priceBlock.querySelector(SELECTORS.PRODUCT_PRICE_TEXT);

    priceEl.innerHTML = `${state.currencySymbol}${Number(variantPrice).toString()}`;
  }

  // Radio-button behaviour across the card's size options
  function markSizeOptionSelected(button) {
    const sizeBlock = button.closest(SELECTORS.SELECT_SIZE_BLOCK);

    sizeBlock.querySelectorAll(SELECTORS.PRODUCT_SIZE_OPTION).forEach((option) => {
      option.classList.remove("selected");
    });

    button.classList.add("selected");
  }

  // Point the add button at the chosen variant and swap it in for "SELECT SIZE"
  function promoteAddButtonForVariant(button, variantId, variantPrice) {
    const sizeBlock = button.closest(SELECTORS.SELECT_SIZE_BLOCK);
    const selectSizeBtn = sizeBlock.querySelector(SELECTORS.SELECT_OPTION_BTN);
    const addToCartBtn = sizeBlock.querySelector(SELECTORS.ADD_TO_CART_BTN);

    addToCartBtn.id = variantId;
    addToCartBtn.dataset.price = variantPrice;

    addToCartBtn.style.display = "block";
    selectSizeBtn.style.display = "none";
  }

  // Keep every product link on the card pointed at the selected variant
  function updateCardLinks(button, variantId) {
    const productCard = button.closest(SELECTORS.PRODUCT_CARD);
    const productLinks = productCard?.querySelectorAll(SELECTORS.PRODUCT_TEXT_LINK);

    if (!productLinks?.length) return;

    productLinks.forEach((productLink) => {
      const url = new URL(productLink.href);
      url.searchParams.set("variant", variantId);
      productLink.href = url.toString();
    });
  }

  function updateCardImage(button, variantId) {
    const productCard = button.closest(SELECTORS.PRODUCT_CARD);
    if (!productCard) return;

    const productImage = productCard.querySelector(SELECTORS.PRODUCT_IMAGE);
    if (!productImage) return;

    const moreDetailBtn = productCard.querySelector(SELECTORS.MORE_DETAIL_BTN);
    const productId = moreDetailBtn?.dataset?.productid || null;

    const product = state.productsById[productId];
    if (!product) return;

    const variant = product.variants.edges.find(v => extractVariantId(v.node.id) === variantId);
    if (!variant) return;

    productImage.src = resolveVariantImage(variant.node.image, product);
  }

  // Size option ids are `${tab}_${variantId}`
  function variantIdFromSizeOption(button) {
    return button.id.split("_").pop();
  }


  // --- Add to cart ---
  function buildCartPayload(variantIds) {
    const cartLineItemProperties = { "_added_from": "tangent-hair-quiz" };
    const cartLineItemQuantity = 1;

    return {
      items: variantIds.map(id => ({
        id: id,
        quantity: cartLineItemQuantity,
        'properties': { ...cartLineItemProperties }
      }))
    };
  }

  function addToCart(element) {
    postToCart(buildCartPayload([element.id]), element);
  }

  function addAllToCart(element) {
    const variantIds = getVisibleProductDetails().map((product) => product.id);
    postToCart(buildCartPayload(variantIds), element);
  }

  // Adds every variant registered under the global add-all bucket.
  // payload is always truthy; empty list still posts (same as before).
  function addSectionToCart(element) {
    const payload = buildCartPayload(state.addAllToCartBySection[GLOBAL_ADD_ALL_SECTION_KEY].list);

    if (payload) {
      postToCart(payload, element);
    }
    else {
      showToast(TEXT.SOLD_OUT);
    }
  }

  function postToCart(payload, element) {
    fetch(API.SHOPIFY_CART_ADD, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(() => {
        trackCartEvent(payload.items.map(item => item.id));
        closeDrawer();
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  }

  // Accumulates every rendered variant into one cart permalink per section.
  // Also toggles the section-level "Add All" button when 2+ variants are registered.
  function registerVariantForAddAll(sectionName, variantId) {
    if (!state.addAllToCartBySection[sectionName]) {
      state.addAllToCartBySection[sectionName] = {
        count: 0,
        cart_link: SHOP_ORIGIN + PATHS.CART_ADD,
        list: []
      };
    }

    const sectionData = state.addAllToCartBySection[sectionName];

    if (sectionData.list.includes(variantId)) return;

    sectionData.count++;
    sectionData.cart_link += `&id[]=${variantId}`;
    sectionData.list.push(variantId);

    // Only worth showing the section's "Add All to Cart" for 2+ products
    const addAllButton = document.getElementById(DOM_ID.sectionAddAllToCart(sectionName));
    if (addAllButton) {
      addAllButton.style.display = sectionData.count > 1 ? "block" : "none";
    }
  }

  function showAddAllToBagBlock() {
    if (addAllToBagBlock) addAllToBagBlock.style.display = "flex";
    refreshAddAllToBagLabel();
  }

  function refreshAddAllToBagLabel() {
    const addAllToBagBtn = document.querySelector(SELECTORS.ADD_ALL_TO_BAG_BTN);
    if (!addAllToBagBtn) return;

    const total = sumProductPrices(getVisibleProductDetails());

    addAllToBagBtn.innerText = `${TEXT.ADD_ALL_TO_BAG} - ${state.currencySymbol}${total}`;
  }

  function sumProductPrices(productDetails) {
    return productDetails.reduce(
      (total, product) => total + product.price,
      0
    );
  }

  // Only buttons on the active tab count — class is addToCart_${tabName}
  function getVisibleProductDetails() {
    const buttons = document.querySelectorAll(`.${addToCartTabClass(state.activeTab)}`);

    return Array.from(buttons).map(button => ({
      id: button.id,
      price: Number(button.dataset.price)
    }));
  }

  function bindAddToCartButtons() {
    document.querySelectorAll(SELECTORS.ADD_TO_CART_BTN_WRAPPER).forEach(button => {
      // Prevent duplicate listener attachment
      if (button.dataset.listenerAttached) return;

      button.addEventListener('click', function () {
        addToCart(this);
      });

      button.dataset.listenerAttached = "true";
    });
  }


  // --- Product detail drawer ---

  function openProductDetail(element) {
    const productId = element.dataset.productid;
    const product = findProductById(productId);
    const selection = readSelectionFromCard(element);

    if (!product) return;

    openDrawer();
    document.querySelector(SELECTORS.DRAWER_CONTENT).innerHTML =
      renderDrawerContent(product, selection);
  }

  function openDrawer() {
    overlay.classList.add("active");
    document.body.style.overflow = "hidden";
  }

  function closeDrawer() {
    overlay.classList.remove("active");
    document.body.style.overflow = "";
  }

  function safeJsonParse(value, fallback = null) {
    if (value == null || value === "") return fallback;

    try {
      return JSON.parse(value);
    } catch (error) {
      console.warn("TangentReco: failed to parse metafield JSON", error);
      return fallback;
    }
  }

  function renderDrawerContent(product, selection) {
    const variantProductUrl = `/products/${product.handle}?source=quiz`;
    const benefitsHtml = renderBenefitList(product);

    return `
              <a
                id="modal_url_tangent_product_${product.id}"
                class="tangent-text-link"
                target="_blank"
                href="${variantProductUrl}"
              ><img class="tangent-flyout-img" src="${resolveVariantImage(null, product)}" /></a>
              ${PAGE.features.showCategoryLabel ? renderCategoryLabel(product) : ""}
              <p class="tangent-flyout-product-title tangent_product_title_text">${escapeHTML(product.title)}</p>
              <p class="flyout-product-price">${selection.productPrice || ""}</p>
              ${product?.shortDescription?.value ? `<p class="flyout-product-description">${product.shortDescription.value}</p>` : ""}
              <button
                class="flyout-tangent-product-add-btn tangent_add_to_cart_button show-on-mobile-only"
                type="button"
                id="${selection.variantId || ""}"
                ${selection.isDisabled ? "disabled" : ""}
                onclick="${NAMESPACE}.addToCart(this)"
                >
                 ${TEXT.FLYOUT_ADD_TO_CART}
              </button>
              ${benefitsHtml
                ? `<p class="benefits-header">${TEXT.KEY_BENEFITS}</p>
              <div class="benefit-list-block">
                ${benefitsHtml}
              </div>`
                : ""}
              ${renderAccordion(product)}
              <button class="back-to-results-btn flyout-exit" onclick="${NAMESPACE}.closeDrawer()">${TEXT.BACK_TO_RESULTS}</button>
            `;
  }

  // Reads the currently selected variant straight off the card that was clicked
  function readSelectionFromCard(element) {
    const productCard = element.closest(SELECTORS.PRODUCT_CONTENT);

    const addToCartBtn = productCard?.querySelector(SELECTORS.ADD_TO_CART_BTN);
    const productPrice = productCard?.querySelector(SELECTORS.PRODUCT_PRICE_TEXT)?.innerText;

    return {
      variantId: addToCartBtn?.id,
      isDisabled: addToCartBtn?.disabled,
      productPrice
    };
  }

  // Product detail drawer accordion, rendered in this order
  const CLINICAL_RESULTS_KEY = "productResults";

  const ACCORDION_SECTIONS = [
    { key: CLINICAL_RESULTS_KEY, value: "CLINICAL RESULTS" },
    { key: "howToUse", value: "HOW TO USE" },
    { key: "productDetail", value: "DETAILS" },
    { key: "productIngredients", value: "INGREDIENTS" }
  ];

  function renderBenefitList(product) {
    const benefits = safeJsonParse(product?.keyBenefits?.value, []);
    if (!Array.isArray(benefits) || !benefits.length) return "";

    return benefits
      .map((benefit) => String(benefit || "").trim())
      .filter(Boolean)
      .map(
        (benefit) => `
                  <div class="product-benefit-item">
                    ${BENEFIT_CHECK_SVG}
                    <span class="product-benefit-text">${benefit}</span>
                  </div>
                `
      )
      .join("");
  }

  function renderAccordion(product) {
    return ACCORDION_SECTIONS
      .map((section) => {
        const rawValue = product?.[section.key]?.value;
        if (rawValue == null || rawValue === "") return "";

        const contentHtml = renderAccordionContent(product, section.key);
        if (!contentHtml) return "";

        return `
                  <div class="product-accordion-item">
                    <button
                      type="button"
                      class="product-accordion-header"
                      data-accordion-title="${section.key}"
                    >
                      <span>${section.value}</span>
                      <span class="product-accordion-icon">
                        ${ACCORDION_CHEVRON_SVG}
                      </span>
                    </button>
          
                    <div class="product-accordion-content">
                      ${contentHtml}
                    </div>
                  </div>
                `;
      })
      .join("");
  }

  function renderAccordionContent(product, key) {
    const parsed = safeJsonParse(product?.[key]?.value, null);
    if (parsed == null) return "";

    if (key === CLINICAL_RESULTS_KEY) {
      return renderClinicalResults(Array.isArray(parsed) ? parsed : []);
    }

    return richTextToHtml(parsed);
  }

  // Each result is "PERCENT::description", optionally with a *footnote suffix
  function renderClinicalResults(results = []) {
    if (!results.length) return "";
    let footnote = "";

    const slides = results
      .map((item) => {
        const [percentage = "", rawDescription = ""] = item.split("::");

        let description = rawDescription.trim();

        if (description.includes("*")) {
          const [cleanDescription, note] = description.split("*");

          description = cleanDescription.trim();

          if (!footnote && note) {
            footnote = `*${note.trim()}`;
          }
        }

        return `
                  <div class="clinical-result-slide">
                    <div class="clinical-result-percent">
                      ${escapeHTML(percentage)}
                    </div>
          
                    <div class="clinical-result-description">
                      ${escapeHTML(description)}
                    </div>
                  </div>
                `;
      })
      .join("");

    return `
              <div class="clinical-results-wrapper">
                <button class="clinical-results-nav prev">❮</button>
          
                <div class="clinical-results-track">
                  ${slides}
                </div>
          
                <button class="clinical-results-nav next">❯</button>
              </div>
            `;
  }

  // Renders a Shopify rich-text metafield node tree to HTML
  function richTextToHtml(node) {
    if (!node) return "";

    const renderChildren = (parent) =>
      parent.children?.map(richTextToHtml).join("") || "";

    switch (node.type) {
      case "root":
        return renderChildren(node);

      case "paragraph":
        return `<p>${renderChildren(node)}</p>`;

      case "list": {
        const listTag = node.listType === "ordered" ? "ol" : "ul";
        return `<${listTag}>${renderChildren(node)}</${listTag}>`;
      }

      case "list-item":
        return `<li>${renderChildren(node)}</li>`;

      case "text": {
        let text = escapeHTML(node.value || "").replace(/\n/g, "<br>");

        if (node.bold) text = `<strong>${text}</strong>`;
        if (node.italic) text = `<em>${text}</em>`;

        return text;
      }

      default:
        return renderChildren(node);
    }
  }


  // --- Tabs & section visibility ---
  function applyInitialTab(response) {
    const quickRoutineTag = "quick_routine";
    const isQuickRoutine = response.tags?.includes(quickRoutineTag);
    const tabName = isQuickRoutine ? TAB.ESSENTIAL : TAB.FULLROUTINE;

    const activeButtonSelector = isQuickRoutine
      ? SELECTORS.ESSENTIAL_TAB_BUTTON
      : SELECTORS.FULLROUTINE_TAB_BUTTON;

    document.querySelector(activeButtonSelector)?.classList.add("active");
    showTabPanel(tabName);

    return tabName;
  }

  function switchTab(button) {
    document.querySelectorAll(SELECTORS.TAB_BUTTON).forEach(tab => {
      tab.classList.remove("active");
    });

    button.classList.add("active");

    state.activeTab = button.dataset.tab === "essentials"
      ? TAB.ESSENTIAL
      : TAB.FULLROUTINE;

    showTabPanel(state.activeTab);
    refreshAddAllToBagLabel();
  }

  function showTabPanel(tabName) {
    if (!essentialsPanel || !fullRoutinePanel) return;

    const showEssentials = tabName === TAB.ESSENTIAL;

    essentialsPanel.style.display = showEssentials ? "block" : "none";
    fullRoutinePanel.style.display = showEssentials ? "none" : "block";
  }

  // Section names embed "essential" or "fullroutine" — convention from API payload
  function tabNameForSection(section) {
    if (PAGE.features.fixedTabName) {
      return PAGE.features.fixedTabName;
    }

    return section.includes(SECTION_TOKEN.ESSENTIAL) ? TAB.ESSENTIAL : TAB.FULLROUTINE;
  }

  // Show the recently-viewed carousel once there is enough of it, else the default
  function toggleRecentlyViewedCarousel() {
    const recentlyViewedMinProducts = { MOBILE: 2, DESKTOP: 5 };
    const productCarousel = document.querySelector(SELECTORS.PRODUCT_CAROUSEL);
    const recentlyViewedSection = document.querySelector(SELECTORS.RECENTLY_VIEWED_SECTION);
    const recentlyViewed = recentlyViewedSection?.querySelectorAll(SELECTORS.RECENTLY_VIEWED_SLIDE) || [];

    const minProducts = window.matchMedia("(max-width: 768px)").matches
      ? recentlyViewedMinProducts.MOBILE
      : recentlyViewedMinProducts.DESKTOP;

    const showRecentlyViewed = recentlyViewed.length >= minProducts;

    productCarousel?.style.setProperty("display", showRecentlyViewed ? "none" : "block");
    recentlyViewedSection?.style.setProperty("display", showRecentlyViewed ? "block" : "none");
  }


  // --- Email results ---

  async function emailResults() {
    const email = document.getElementById(ELEMENT_IDS.RESULTS_EMAIL_INPUT)?.value?.trim();
    const errorLabel = document.querySelector(SELECTORS.EMAIL_ERROR);

    const error = validateEmail(email);

    if (error) {
      showEmailError(errorLabel, error);
      return;
    }

    errorLabel.style.display = "none";

    try {
      const response = await fetch(
        API.QUIZ_RESPONSES,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(buildEmailPayload(email))
        }
      );

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      await response.json();
      emailResultBlock.style.display = "none";
      trackCustomEvent("email_my_result");
    } catch (error) {
      console.error("Failed to send email", error);
    }
  }

  function validateEmail(email) {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) return TEXT.EMAIL_REQUIRED;
    if (!emailPattern.test(email)) return TEXT.EMAIL_INVALID;
    return null;
  }

  function buildEmailPayload(email) {
    const emailCaptureFormIds = {
      US: "6a2a97a48ccc13a386a442db",
      GB: "6a54c31f8ccc13a386bdc945",
      AU: "6a54cd838ccc13a386bdefd7"
    };
    const shopId = document.querySelector(SELECTORS.EMAIL_FIELD)?.dataset.shopId;
    const countryCode = window.Shopify?.country;

    return {
      shop_id: shopId,
      form_id: emailCaptureFormIds[countryCode],
      resp_id: new URL(window.location.href).searchParams.get("resp_id"),
      responses: {
        form: {
          email
        }
      }
    };
  }

  function showEmailError(errorLabel, message) {
    errorLabel.innerText = message;
    errorLabel.style.display = "block";
  }


  // --- UI helpers ---
  function escapeHTML(str) {
    if (!str) return "";

    const htmlEscapePattern = /[&<>"']/g;
    const htmlEscapeMap = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;"
    };

    return str.replace(htmlEscapePattern, function (m) {
      return htmlEscapeMap[m];
    });
  }

  function trimWords(text, wordLimit = 8) {
    const words = text.trim().split(/\s+/);

    return words.slice(0, wordLimit).join(" ") + "...";
  }

  function revealDataDependentElements() {
    document.querySelectorAll(SELECTORS.LOAD_ON_DATA).forEach(element => {
      element.style.display = "block";
    });
  }

  // Transient message banner, auto-hidden by removing the "show" class
  function showToast(message) {
    const toastVisibleDurationMs = 5000;
    const toast = document.querySelector(SELECTORS.TOAST_MESSAGE);

    toast.innerHTML = message;
    toast.className = "show";

    setTimeout(function () {
      toast.className = toast.className.replace("show", "");
    }, toastVisibleDurationMs);
  }

  function openChatBot() {
    document.getElementById(ELEMENT_IDS.CHATBOT_WIDGET_ICON)?.click();
  }


  // --- Analytics ---
  // Wait until the Shopify Customer Privacy API is ready (max 10s)
  function whenShopifyPrivacyReady(callback) {
    const shopifyPrivacyPollIntervalMs = 500;
    const shopifyPrivacyPollTimeoutMs = 10000;

    const interval = setInterval(() => {
      if (window.Shopify && window.Shopify.customerPrivacy != null) {
        clearInterval(interval);
        callback();
      }
    }, shopifyPrivacyPollIntervalMs);

    setTimeout(() => {
      clearInterval(interval);
    }, shopifyPrivacyPollTimeoutMs);
  }

  function requestAnalyticsConsent() {
    whenShopifyPrivacyReady(() => {
      state.analyticsConsent = window.Shopify.customerPrivacy.analyticsProcessingAllowed();
    });
  }

  function trackCartEvent(variantIds) {
    if (!state.analyticsConsent) return;

    const addedProducts = variantIds
      .map((variantId) => describeAddedProduct(variantId))
      .filter(Boolean);

    const eventName = addedProducts.length > 1 ? "added_all_to_cart" : "added_to_cart";
    trackCustomEvent(eventName, { products: addedProducts });
  }

  // Track add-to-cart / add-all-to-cart events when consent has been granted
  function trackCustomEvent(eventName, payload = {}) {
    if (!state.analyticsConsent) return;

    fetch(API.ANALYTICS_TRACK, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        "resp_id": state.quizResponseId,
        "event_name": eventName,
        "event_type": "click",
        "recommendation_url": `${SHOP_ORIGIN}${PATHS.RECOMMENDATION}?resp_id=${state.quizResponseId}`,
        "attributes": payload
      })
    });
  }

  function describeAddedProduct(variantId) {
    const product = findProductByVariantId(variantId);
    const variant = findVariant(product, variantId);

    if (!product || !variant) return null;

    return {
      "product_id": extractVariantId(product.id),
      "variant_id": extractVariantId(variantId),
      "price": getVariantPrice(variant),
      "product_name": product.title,
      "variant_name": variant.title
    };
  }


  // --- Event listeners ---

  function registerEventListeners() {
    // Click outside the drawer content closes it
    overlay?.addEventListener("click", (e) => {
      if (e.target === overlay) {
        closeDrawer();
      }
    });

    // Delegated: expand / collapse a product detail accordion
    document.addEventListener("click", (e) => {
      const header = e.target.closest(SELECTORS.ACCORDION_HEADER);
      if (!header) return;

      header.closest(SELECTORS.ACCORDION_ITEM).classList.toggle("active");
    });

    // Delegated: page the clinical results carousel one slide at a time
    document.addEventListener("click", (e) => {
      const prevBtn = e.target.closest(SELECTORS.CLINICAL_RESULTS_PREV);
      const nextBtn = e.target.closest(SELECTORS.CLINICAL_RESULTS_NEXT);

      if (!prevBtn && !nextBtn) return;

      const wrapper = e.target.closest(SELECTORS.CLINICAL_RESULTS_WRAPPER);
      const track = wrapper?.querySelector(SELECTORS.CLINICAL_RESULTS_TRACK);

      if (!track) return;

      const slideWidth = track.clientWidth;

      track.scrollBy({
        left: prevBtn ? -slideWidth : slideWidth,
        behavior: "smooth"
      });
    });
  }


  // --- Public API ---
  // The only names this file adds to the page. Every entry exists because an
  // inline onclick attribute needs it — either in the Liquid section or in
  // the markup generated above.

  const publicApi = IS_GIFT
    ? {
      closeDrawer,
      openProductDetail,
      selectVariant,
      toggleSizeOptions,
      addToCart,
      openChatBot
    }
    : {
      switchTab,
      closeDrawer,
      emailResults,
      addAllToCart,
      addSectionToCart,
      openProductDetail,
      selectVariant,
      toggleSizeOptions,
      addToCart,
      openChatBot
    };

  const LEGACY_GLOBAL_ALIASES = IS_GIFT
    ? {
      closeDrawer: publicApi.closeDrawer,
      handleMoreDetail: publicApi.openProductDetail,
      handleVariantSelection: publicApi.selectVariant,
      handleSelectSize: publicApi.toggleSizeOptions,
      addToCart: publicApi.addToCart,
      openChatBot: publicApi.openChatBot
    }
    : {
      handleTabClick: publicApi.switchTab,
      closeDrawer: publicApi.closeDrawer,
      handleEmailResult: publicApi.emailResults,
      handleAddAllToCart: publicApi.addAllToCart,
      addAllTocartFunction: publicApi.addSectionToCart,
      handleMoreDetail: publicApi.openProductDetail,
      handleVariantSelection: publicApi.selectVariant,
      handleSelectSize: publicApi.toggleSizeOptions,
      addToCart: publicApi.addToCart,
      openChatBot: publicApi.openChatBot
    };

  function publish() {
    window[NAMESPACE] = publicApi;

    if (!EXPOSE_LEGACY_GLOBALS) return;

    Object.entries(LEGACY_GLOBAL_ALIASES).forEach(([name, fn]) => {
      window[name] = fn;
    });
  }


  // --- Bootstrap ---

  publish();
  registerEventListeners();

  // Entry: wait for DOM, then load quiz recommendation data
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      init();
    });
  } else {
    init();
  }

})(window, document);
